import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Slider,
  Card,
  CardMedia,
  IconButton,
  Alert,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  FormHelperText,
  Snackbar
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { Problem, ProblemGroup } from '../../../services/sessionService';
import { getBaseURL } from '../../../services/apiClient';
import { getToken } from '../../../utils/jwtUtils';
import {
  FormProvider,
  FormTextField,
  FormRichTextField,
  FormSelectField
} from '../../../components/forms';
import MultipleChoiceOptionsList, { ProblemOption } from './MultipleChoiceOptionsList';

interface ProblemFormProps {
  initialData?: Partial<Problem>;
  problemGroups?: ProblemGroup[];
  onSubmit?: (data: Omit<Problem, 'id' | 'createdAt' | 'updatedAt'> | Problem) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isNew?: boolean;
}

// Define validation schema
const problemSchema = yup.object().shape({
  problemStatement: yup.string().required('Problem statement is required'),
  correctSolution: yup.string().when(['problemType'], {
    is: (problemType: string) => problemType === 'open_ended',
    then: () => yup.string().required('Correct solution is required'),
    otherwise: () => yup.string()
  }),
  difficultyLevel: yup.number()
    .required('Difficulty level is required')
    .min(1, 'Difficulty level must be at least 1')
    .max(5, 'Difficulty level cannot exceed 5'),
  groupId: yup.number().required('Problem group is required'),
  sessionId: yup.number().required('Session ID is required'),
  imagePath: yup.string().nullable(),
  problemType: yup.string()
    .required('Problem type is required')
    .oneOf(['open_ended', 'multiple_choice'], 'Invalid problem type'),
  options: yup.array().when(['problemType'], {
    is: (problemType: string) => problemType === 'multiple_choice',
    then: () => yup.array()
      .of(
        yup.object().shape({
          id: yup.mixed().required(),
          optionText: yup.string().required('Option text is required'),
          isCorrect: yup.boolean().required(),
          optionOrder: yup.number().required()
        })
      )
      .min(2, 'At least 2 options are required')
      .test(
        'one-correct',
        'Exactly one option must be marked as correct',
        options => options ? options.filter(o => o.isCorrect).length === 1 : false
      ),
    otherwise: () => yup.array()
  })
});

// Form data type
interface ProblemFormData {
  problemStatement: string;
  correctSolution?: string;
  difficultyLevel: number;
  groupId: number;
  sessionId: number;
  imagePath?: string | null;
  problemType: string;
  options?: ProblemOption[];
}

const ProblemForm = ({
  initialData,
  problemGroups = [],
  onSubmit = () => {
    // Default empty submit handler
  },
  onCancel,
  isLoading = false
}: ProblemFormProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialData?.imagePath || null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Get the API URL with proper formatting
  const getApiUrl = () => {
    let baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    
    // If baseUrl doesn't have /api, add it
    if (!baseUrl.endsWith('/api')) {
      baseUrl = baseUrl + '/api';
    }
    
    return baseUrl;
  };
  
  // API URL available for future use
  getApiUrl();

  // Initialize React Hook Form
  const methods = useForm<ProblemFormData>({
    // @ts-ignore - yupResolver type mismatch with our form data
    resolver: yupResolver(problemSchema),
    defaultValues: {
      problemStatement: initialData?.problemStatement || '',
      correctSolution: initialData?.correctSolution || '',
      difficultyLevel: initialData?.difficultyLevel || 3,
      groupId: initialData?.groupId || (problemGroups.length > 0 ? Number(problemGroups[0].id) : 0),
      sessionId: initialData?.sessionId || (problemGroups.length > 0 ? Number(problemGroups[0].sessionId) : 0),
      imagePath: initialData?.imagePath || null,
      problemType: initialData?.problemType || 'open_ended',
      options: initialData && initialData.options && initialData.options.length > 0
        ? [...initialData.options]
        : [
            { id: 'temp-1', optionText: '', isCorrect: true, optionOrder: 1 },
            { id: 'temp-2', optionText: '', isCorrect: false, optionOrder: 2 }
          ]
    }
  });
  
  const { 
    control, 
    watch, 
    setValue, 
    formState: { errors }
  } = methods;
  
  // Update form values when problemGroups are loaded
  useEffect(() => {
    console.log('ProblemForm: problemGroups changed', {
      count: problemGroups.length,
      groups: problemGroups,
      initialData
    });

    if (problemGroups.length > 0) {
      // If we have initialData with a groupId, make sure it's set
      if (initialData?.groupId) {
        console.log('Setting groupId from initialData:', initialData.groupId);
        const groupExists = problemGroups.find(g => Number(g.id) === Number(initialData.groupId));
        if (groupExists) {
          setValue('groupId', Number(initialData.groupId));
          setValue('sessionId', Number(groupExists.sessionId));
        } else {
          console.warn('Group not found in problemGroups:', initialData.groupId);
        }
      }
      // If no initialData, set to first group
      else {
        console.log('No initialData, setting to first group');
        const firstGroupId = Number(problemGroups[0].id);
        const firstGroupSessionId = Number(problemGroups[0].sessionId);
        setValue('groupId', firstGroupId);
        setValue('sessionId', firstGroupSessionId);
      }
    } else {
      console.warn('ProblemForm: problemGroups is empty');
    }
  }, [problemGroups, initialData, setValue]);

  // Update form values and preview when initialData changes
  useEffect(() => {
    if (initialData) {
      if (initialData.sessionId) {
        setValue('sessionId', initialData.sessionId);
      }

      if (initialData.groupId) {
        setValue('groupId', initialData.groupId);
      }
      
      // Set image preview if available
      if (initialData.imagePath) {
        // Normalize image path to use S3 directly
        const normalizeImagePath = (path: string) => {
          console.log('Normalizing image path:', path);
          
          // Case 1: Already a full URL (S3 URL or external image URL)
          if (path.startsWith('http')) {
            console.log('Path is already a full URL (S3 or external)');
            return path;
          }
          
          // Get just the filename without any path
          const filename = path.split('/').pop();
          
          if (!filename) {
            console.error('Invalid image path:', path);
            return '';
          }
          
          // Use S3 bucket name from environment variable if available
          const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'mathwiz-images';
          const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
          
          // Return the S3 URL in the appropriate format
          const s3Url = `https://${bucketName}.s3.${region}.amazonaws.com/uploads/${filename}`;
          console.log('Using S3 URL:', s3Url);
          return s3Url;
        };
        
        const imageUrl = normalizeImagePath(initialData.imagePath);
        console.log('Loading image from URL:', imageUrl);
        setPreviewUrl(imageUrl);
      }
    }
  }, [initialData, setValue]);

  const groupId = watch('groupId');
  const imagePath = watch('imagePath');
  const problemType = watch('problemType');
  const options = watch('options');
  
  // Initialize default options when switching to multiple choice
  useEffect(() => {
    if (problemType === 'multiple_choice' && (!options || options.length === 0)) {
      setValue('options', [
        { id: 'temp-1', optionText: '', isCorrect: true, optionOrder: 1 },
        { id: 'temp-2', optionText: '', isCorrect: false, optionOrder: 2 }
      ]);
    }
  }, [problemType, options, setValue]);

  // Update session ID when group changes - this is handled by the backend validation now
  // We retain this for UI consistency, but the backend will enforce the relationship
  useEffect(() => {
    if (groupId) {
      const selectedGroup = problemGroups.find(g => g.id === groupId);
      if (selectedGroup) {
        setValue('sessionId', selectedGroup.sessionId);
      }
    }
  }, [groupId, problemGroups, setValue]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Create a preview URL for immediate display
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Upload the file immediately
      try {
        setUploading(true);
        setUploadError(null);
        
        const formData = new FormData();
        formData.append('image', file);
        
        const uploadUrl = getBaseURL();
        console.log('Using upload URL:', uploadUrl);
        
        const response = await axios.post(
          `${uploadUrl}/api/upload/file`, 
          formData, 
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${getToken()}`
            }
          }
        );
        
        // Set the image path received from the server
        setValue('imagePath', response.data.filePath, { shouldValidate: true });
        setUploadError(null);
        console.log('Image uploaded successfully:', response.data.filePath);
        setSuccessMessage('Image uploaded successfully!');
      } catch (error: unknown) {
        console.error('Error uploading image:', error);

        // Provide more detailed error information
        let errorMessage = 'Failed to upload image. ';

        if (axios.isAxiosError(error)) {
          if (error.response) {
            // The request was made and the server responded with a status code
            console.error('Response error:', error.response.status, error.response.data);
            errorMessage += `Server responded with status ${error.response.status}: ${(error.response.data as { message?: string })?.message || error.message}`;

            if (error.response.status === 401) {
              errorMessage += '. You may need to log in again.';
            }
          } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received:', error.request);
            errorMessage += 'No response received from server. Check your network connection.';
          } else {
            // Something happened in setting up the request
            console.error('Request error:', error.message);
            errorMessage += error.message;
          }
        } else if (error instanceof Error) {
          errorMessage += error.message;
        } else {
          errorMessage += 'An unknown error occurred.';
        }

        setUploadError(errorMessage);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setValue('imagePath', null, { shouldValidate: true });
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getDifficultyLabel = (value: number) => {
    switch (value) {
      case 1: return 'Very Easy';
      case 2: return 'Easy';
      case 3: return 'Medium';
      case 4: return 'Hard';
      case 5: return 'Very Hard';
      default: return '';
    }
  };

  // Handle form submission
  const processSubmit = (data: ProblemFormData) => {
    // Construct the problem data
    const problemData = {
      ...(initialData?.id ? { id: initialData.id } : {}),
      ...data
    } as Problem | Omit<Problem, 'id' | 'createdAt' | 'updatedAt'>;

    onSubmit(problemData);
  };

  return (
    // @ts-ignore - FormProvider type issue with resolver
    <FormProvider methods={methods} onSubmit={processSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormSelectField
            name="groupId"
            label="Problem Group"
            options={problemGroups
              .filter(group => group.id != null)
              .map(group => ({
                value: Number(group.id),
                label: group.title
              }))}
            disabled={problemGroups.length === 0}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Problem Type</InputLabel>
            <Controller
              name="problemType"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Problem Type"
                >
                  <MenuItem value="open_ended">Open Ended</MenuItem>
                  <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                </Select>
              )}
            />
            {errors.problemType && (
              <FormHelperText error>{errors.problemType.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <FormRichTextField
            name="problemStatement"
            label="Problem Statement"
            rows={4}
            required
            helperText="Use the formatting tools and math symbols to create clear problem statements"
          />
        </Grid>
        
        {problemType === 'open_ended' ? (
          <Grid item xs={12} md={6}>
            <FormTextField
              name="correctSolution"
              label="Correct Solution"
              helperText="The exact answer that will be checked against student submissions"
              required
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <MultipleChoiceOptionsList
              options={options || []}
              onChange={(newOptions) => setValue('options', newOptions)}
              error={errors.options?.message as string}
            />
          </Grid>
        )}
        
        <Grid item xs={12} md={6}>
          <Controller
            name="difficultyLevel"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography gutterBottom>
                  Difficulty Level: {getDifficultyLabel(field.value)}
                </Typography>
                <Slider
                  {...field}
                  onChange={(_, value) => field.onChange(value)}
                  step={1}
                  marks
                  min={1}
                  max={5}
                  valueLabelDisplay="auto"
                  valueLabelFormat={getDifficultyLabel}
                />
                {errors.difficultyLevel && (
                  <Typography color="error" variant="caption">
                    {errors.difficultyLevel.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Problem Image (Optional)
          </Typography>
          
          <input
            ref={fileInputRef}
            accept="image/*"
            id="image-upload"
            type="file"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            
            {(previewUrl || imagePath) && (
              <IconButton 
                color="error" 
                onClick={handleRemoveImage}
                sx={{ ml: 2 }}
                disabled={uploading}
              >
                <DeleteIcon />
              </IconButton>
            )}
            
            {uploading && <CircularProgress size={24} sx={{ ml: 2 }} />}
          </Box>
          
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          
          {previewUrl && (
            <Card sx={{ maxWidth: 345, mb: 2 }}>
              <CardMedia
                component="img"
                height="200"
                image={previewUrl}
                alt="Problem image preview"
                sx={{ objectFit: 'contain' }}
                onError={(_e) => {
                  console.error('Image failed to load:', previewUrl);
                  setUploadError(`Failed to load image from S3: "${previewUrl}". Please check your S3 bucket permissions and CORS settings.`);
                }}
              />
              {imagePath && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                  Image uploaded successfully
                </Typography>
              )}
            </Card>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            {onCancel && (
              <Button 
                type="button" 
                onClick={onCancel}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
            )}
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  {initialData?.id ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                initialData?.id ? 'Save Changes' : 'Create Problem'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
      
      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </FormProvider>
  );
};

export default ProblemForm;